/**
 * TrivenyConsentGate — first-login (and re-version) enforcement for the 3
 * Triveny doc types: Terms of Use, Privacy Policy, AI Disclaimers.
 *
 * Mount once per authenticated session in App.tsx for Triveny users; on
 * mount it calls GET /api/consent/status with the platform JWT. The
 * backend route (consent_management.py:_resolve_consent_actor) detects
 * the platform JWT and returns a PlatformConsentStatus shape:
 *
 *   { terms_needs_update, privacy_needs_update, ai_needs_update,
 *     terms_current_version, privacy_current_version, ai_current_version, ... }
 *
 * For each `*_needs_update=true` doc, we open a modal step that fetches the
 * doc content (GET /api/consent/documents/{type}), shows it, requires a
 * typed name as the signature, and POSTs /api/consent/sign with the
 * platform JWT (backend writes to platform_shared.tenant_consent_signatures
 * via the dual-auth branch). On completion the modal closes; subsequent
 * mounts no-op because the status flips to all_consents_current=true.
 *
 * The component renders nothing when no docs need signing.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, FileText, Loader2, Sparkles } from 'lucide-react';
import { config } from '@/src/config';
import { tvTokens } from '@/src/platform/shared/components/triveny';

type DocType = 'terms_of_use' | 'privacy_policy' | 'ai_disclaimers';

interface PlatformConsentStatus {
  terms_current_version?: string | null;
  terms_signed_version?: string | null;
  terms_needs_update: boolean;
  privacy_current_version?: string | null;
  privacy_signed_version?: string | null;
  privacy_needs_update: boolean;
  ai_current_version?: string | null;
  ai_signed_version?: string | null;
  ai_needs_update: boolean;
  all_consents_current: boolean;
}

interface ConsentDocResponse {
  id: number;
  document_type: string;
  version: string;
  title: string;
  content: string;
  effective_date?: string;
}

const DOC_META: Record<
  DocType,
  { label: string; eyebrow: string; icon: React.ComponentType<{ size?: number }> }
> = {
  terms_of_use: { label: 'Terms of Use', eyebrow: 'Step 1 of 3', icon: FileText },
  privacy_policy: { label: 'Privacy Policy', eyebrow: 'Step 2 of 3', icon: FileText },
  ai_disclaimers: { label: 'AI Disclaimers', eyebrow: 'Step 3 of 3', icon: Sparkles },
};

function inlineMd(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:#f3f3ef;padding:1px 5px;border-radius:3px;font-family:JetBrains Mono,monospace;font-size:12px">$1</code>');
}

function renderMd(md: string): React.ReactNode {
  const lines = md.split('\n');
  const out: React.ReactNode[] = [];
  let listBuf: string[] = [];
  const flushList = () => {
    if (!listBuf.length) return;
    out.push(
      <ul key={`l${out.length}`} style={{ listStyle: 'disc outside', paddingLeft: 22, margin: '8px 0' }}>
        {listBuf.map((it, i) => (
          <li key={i} style={{ marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: inlineMd(it) }} />
        ))}
      </ul>,
    );
    listBuf = [];
  };
  lines.forEach((line, idx) => {
    if (line.startsWith('# ')) {
      flushList();
      out.push(
        <h1 key={idx} style={{ fontFamily: tvTokens.fontDisplay, fontSize: 24, fontWeight: 600, letterSpacing: '-0.025em', margin: '16px 0 10px', color: tvTokens.ink }}>
          {line.slice(2)}
        </h1>,
      );
    } else if (line.startsWith('## ')) {
      flushList();
      out.push(
        <h2 key={idx} style={{ fontFamily: tvTokens.fontDisplay, fontSize: 17, fontWeight: 600, letterSpacing: '-0.015em', margin: '20px 0 6px', color: tvTokens.ink }}>
          {line.slice(3)}
        </h2>,
      );
    } else if (line.startsWith('- ')) {
      listBuf.push(line.slice(2));
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      out.push(
        <p key={idx} style={{ fontSize: 14, lineHeight: 1.6, margin: '8px 0', color: tvTokens.ink700 }} dangerouslySetInnerHTML={{ __html: inlineMd(line) }} />,
      );
    }
  });
  flushList();
  return <>{out}</>;
}

interface TrivenyConsentGateProps {
  /** Display name for the prefilled signature input. */
  userName?: string | null;
}

export default function TrivenyConsentGate({ userName }: TrivenyConsentGateProps) {
  const [pending, setPending] = useState<{ type: DocType; version: string | null }[] | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [doc, setDoc] = useState<ConsentDocResponse | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Initial /status fetch — figure out which docs need signing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setPending([]); // not signed in
          return;
        }
        const res = await fetch(`${config.weaveinApi.baseUrl}/api/consent/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const status = (await res.json()) as PlatformConsentStatus;
        if (cancelled) return;
        const need: { type: DocType; version: string | null }[] = [];
        if (status.terms_needs_update) need.push({ type: 'terms_of_use', version: status.terms_current_version ?? null });
        if (status.privacy_needs_update) need.push({ type: 'privacy_policy', version: status.privacy_current_version ?? null });
        if (status.ai_needs_update) need.push({ type: 'ai_disclaimers', version: status.ai_current_version ?? null });
        setPending(need);
      } catch (e: any) {
        if (!cancelled) setStatusError(e?.message || 'Failed to load consent status');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const current = pending && stepIndex < pending.length ? pending[stepIndex] : null;
  const totalSteps = pending?.length ?? 0;

  // Fetch the active doc for the current step.
  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    setDoc(null);
    setDocError(null);
    setDocLoading(true);
    (async () => {
      try {
        // product=triveny → the Triveny.ai-branded docs (consent_documents is
        // a shared table; the default returns WeaveIn/Harmony content).
        const res = await fetch(
          `${config.weaveinApi.baseUrl}/api/consent/documents/${encodeURIComponent(current.type)}?product=triveny`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as ConsentDocResponse;
        if (!cancelled) setDoc(body);
      } catch (e: any) {
        if (!cancelled) setDocError(e?.message || 'Failed to load document');
      } finally {
        if (!cancelled) setDocLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [current?.type]);

  // Defaults the typed signature to the user's display name (typing replaces it).
  useEffect(() => {
    setSignature(userName?.trim() || '');
  }, [stepIndex, userName]);

  const eyebrow = useMemo(() => {
    if (!current || !pending) return '';
    return `Step ${stepIndex + 1} of ${totalSteps}`;
  }, [current, pending, stepIndex, totalSteps]);

  if (statusError) {
    // Hard error — don't block the user, just log. They'll see the
    // legal hub in Preferences and can sign there.
    console.error('[TrivenyConsentGate] status error:', statusError);
    return null;
  }
  if (!pending || pending.length === 0) return null;
  // Once we've stepped past the last pending doc, the modal MUST dismiss.
  // Prior bug: without this guard, clicking the final "Accept and enter
  // Triveny" button advanced stepIndex past pending.length but the modal
  // kept rendering with current=null + the last-fetched doc still bound,
  // and the Accept button stayed enabled — so users could (and did) fire
  // duplicate POST /sign requests until they gave up and reloaded.
  if (stepIndex >= pending.length) return null;

  const handleAccept = async () => {
    if (!doc) return;
    if (signature.trim().length < 2) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.weaveinApi.baseUrl}/api/consent/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ document_id: doc.id, signature_text: signature.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Advance to next step; clears doc state. The guard above handles
      // the case where this advance pushes stepIndex past pending.length —
      // the next render returns null and the modal dismisses cleanly.
      setStepIndex((i) => i + 1);
    } catch (e: any) {
      setDocError(e?.message || 'Could not record acceptance — try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const meta = current ? DOC_META[current.type] : null;
  const Icon = meta?.icon ?? FileText;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        background: 'rgba(20,23,28,0.52)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 'min(860px, 100%)',
          maxHeight: '90vh',
          background: tvTokens.surface,
          borderRadius: tvTokens.rXl,
          border: `1px solid ${tvTokens.border}`,
          boxShadow: '0 28px 64px -20px rgba(20,23,28,0.4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 18px',
            borderBottom: `1px solid ${tvTokens.borderMuted}`,
          }}
        >
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: tvTokens.r,
              background: tvTokens.accent,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={14} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: tvTokens.fontMono,
                fontSize: 10,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: tvTokens.ink500,
              }}
            >
              {eyebrow} · Welcome to Triveny.ai
            </div>
            <div
              style={{
                fontFamily: tvTokens.fontDisplay,
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: tvTokens.ink,
              }}
            >
              {meta?.label}
              {doc?.version && (
                <span
                  style={{
                    fontFamily: tvTokens.fontMono,
                    fontSize: 11,
                    fontWeight: 500,
                    color: tvTokens.ink500,
                    marginLeft: 8,
                  }}
                >
                  v{doc.version}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: '18px 26px', overflowY: 'auto', flex: 1, background: tvTokens.paper }}>
          {docLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: tvTokens.ink500, fontSize: 13 }}>
              <Loader2 size={16} className="animate-spin" /> Loading {meta?.label}…
            </div>
          )}
          {docError && !docLoading && (
            <div style={{ color: '#b91c1c', fontSize: 13 }}>{docError}</div>
          )}
          {doc && !docLoading && !docError && renderMd(doc.content)}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: `1px solid ${tvTokens.borderMuted}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <label
            style={{
              fontFamily: tvTokens.fontMono,
              fontSize: 10,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: tvTokens.ink500,
            }}
          >
            Type your full name to accept
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Your full name"
              disabled={submitting || docLoading || !doc}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontFamily: tvTokens.fontSans,
                fontSize: 13.5,
                color: tvTokens.ink,
                background: tvTokens.surface,
                border: `1px solid ${tvTokens.border}`,
                borderRadius: tvTokens.r,
                outline: 'none',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = tvTokens.ink;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = tvTokens.border;
              }}
            />
            <button
              type="button"
              disabled={submitting || docLoading || !doc || signature.trim().length < 2}
              onClick={handleAccept}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '8px 16px',
                background:
                  submitting || docLoading || !doc || signature.trim().length < 2
                    ? tvTokens.ink400
                    : tvTokens.accent,
                color: '#fff',
                border: 0,
                borderRadius: tvTokens.r,
                fontFamily: tvTokens.fontSans,
                fontSize: 13,
                fontWeight: 500,
                cursor:
                  submitting || docLoading || !doc || signature.trim().length < 2
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {submitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <CheckCircle2 size={14} />
              )}
              {stepIndex + 1 < totalSteps ? 'Accept and continue' : 'Accept and enter Triveny'}
            </button>
          </div>
          <span style={{ fontSize: 11, color: tvTokens.ink500 }}>
            Your acceptance is logged with your IP and timestamp for audit.
          </span>
        </div>
      </div>
    </div>
  );
}
